import { Suspense, lazy, useState } from 'react';

import { Box, Link, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  useGetUserSettingsQuery,
  usePatchUserSettingsMutation,
} from '@/store/api/userApi';
import { useAppSelector } from '@/store/hooks';

import CleanSightLogoDark from '@/assets/CleanSight-logo-dark.png';
import CleanSightLogoLight from '@/assets/CleanSight-logo-light.png';
import { useSnackbar } from '@/context/snackbar/useSnackbar';
import { MSA_AGREEMENT_LINK } from '@/utils/constants';

import { logoSx } from './CleanSight.styles';

const ConfirmationDialog = lazy(() =>
  import('@/components/common/ConfirmationDialog').then(module => ({
    default: module.ConfirmationDialog,
  }))
);

export const CleanSight: React.FC = () => {
  const { addErrorSnackbar } = useSnackbar();
  const { darkMode } = useAppSelector(state => state.common);

  const CleanSightLogo = darkMode ? CleanSightLogoDark : CleanSightLogoLight;

  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: userSettings, isLoading: isUserSettingsLoading } =
    useGetUserSettingsQuery();
  const { cleansightMsaSigned } = userSettings || {};

  const [patchUserSettings, { isLoading: isPatchUserSettingsLoading }] =
    usePatchUserSettingsMutation();

  const navigateToCleanSight = () => {
    window.open(
      import.meta.env.VITE_CLEANSIGHT_URL,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleLogoClick = () => {
    if (cleansightMsaSigned) {
      navigateToCleanSight();
    } else {
      setDialogOpen(true);
    }
  };

  const onAccept = async () => {
    try {
      const result = await patchUserSettings({
        cleansightMsaSigned: true,
      }).unwrap();

      // shouldn't happen but make sure BE stored the agreement properly
      if (result.cleansightMsaSigned) {
        setDialogOpen(false);
        navigateToCleanSight();
      }
    } catch (err) {
      addErrorSnackbar(err as FetchBaseQueryError);
    }
  };

  const isLoading = isUserSettingsLoading || isPatchUserSettingsLoading;

  return (
    <>
      <Box
        sx={logoSx}
        component="img"
        src={CleanSightLogo}
        alt="CleanSight logo"
        onClick={handleLogoClick}
      />
      {dialogOpen && (
        <Suspense fallback={null}>
          <ConfirmationDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onConfirm={onAccept}
            title="Master Subscription Agreement"
            confirmButtonLabel="Yes"
            cancelButtonLabel="No"
            cancelVariant="danger"
            isLoading={isLoading}
          >
            <Typography variant="body2" color="text.secondary" mb={1}>
              You are accessing Additional Services as contemplated by your
              CleanTrade Access Documentation. These services are provided by
              REsurety Markets Affiliate, REsurety Insights LLC.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              To access these services, you must have agreed to the terms of a
              Master Subscription Agreement (&quot;MSA&quot;) or similar access
              document with REsurety Insights. If an active agreement already
              exists with respect to your organization&lsquo;s access to the
              Discover and Manage applications, you may click &quot;Yes&quot;
              below and the existing agreement will govern your access to the
              Additional Services.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              In all other cases, if you click &quot;Yes&quot;, you agree that
              you have the authority to bind the organization on whose behalf
              you are accessing CleanTrade and that this confirmation shall form
              your Order Form with respect to access to Discover and Manage.
            </Typography>
            <Link
              target="_blank"
              rel="noreferrer"
              href={MSA_AGREEMENT_LINK}
              underline="hover"
            >
              Master Subscription Agreement (&quot;MSA&quot;)
            </Link>
            <Typography variant="body2" color="text.secondary" mt={1}>
              If you do not wish to proceed, please click &quot;No&quot;.
            </Typography>
          </ConfirmationDialog>
        </Suspense>
      )}
    </>
  );
};
